import OrderDTO from "@/dto/order/OrderDTO";
import OrderStatus from "@/enum/OrderStatus";
import { checkUndeliverable, isAbnormalAddress } from "@/utils/OrderUtils";
import OrderTag from "@/enum/OrderTag";
import OrderTagNameMap from "@/map/OrderTagNameMap";
import { companySettingStore } from "@/store/hooks";
import { UndeliverableAreaCode } from "@/services/undeliverableAreaService";
import GlobalStore from "@/store";
import { PrintRule, PrintRuleLevel } from "../types";
import {
    isAnyOrderWithAliCanNotShipTag,
    isAnyOrderWithAliWarehouse,
    isAnyOrderWithCanNotShipTag,
    isAnyOrderWithJDPetItem,
    isAnyOrderWithPddWarehouse,
    isAnyOrderWithTbCanNotShipTag,
} from "@/utils/OrderTagUtils";

export function buildPrintRules(params: {
    addressFilterKeywords: string[];
    undeliverableAreaCodes: UndeliverableAreaCode[];
    globalStore: typeof GlobalStore;
}): PrintRule[] {
    const { addressFilterKeywords, undeliverableAreaCodes, globalStore } = params;

    return [
        {
            id: "pending_payment",
            label: "未付款",
            level: PrintRuleLevel.OPTIONAL,
            match: (o) => o.status === OrderStatus.PENDING_PAYMENT,
        },
        {
            id: "dropshipping",
            label: "代发订单",
            level: PrintRuleLevel.OPTIONAL,
            match: (o) => !!o.retailerDropShippingInfo,
        },
        {
            id: "canceled",
            label: "已取消",
            level: PrintRuleLevel.OPTIONAL,
            match: (o) => o.status === OrderStatus.CANCELED,
        },
        {
            id: "refunding",
            label: "退款中",
            level: PrintRuleLevel.OPTIONAL,
            match: (o) => o.isRefunding,
        },
        {
            id: "printed",
            label: "已打印 / 已获取单号",
            level: PrintRuleLevel.OPTIONAL,
            match: (o) => o.isWaybillPrinted || !!o.hasEwaybillNo,
        },
        {
            id: "blacklist",
            label: "黑名单订单",
            level: PrintRuleLevel.OPTIONAL,
            match: async (o) => {
                const res = await companySettingStore.ensureHintBlacklistDetail(o);
                return !!res;
            },
        },
        {
            id: "abnormal_address",
            label: "地址异常",
            level: PrintRuleLevel.OPTIONAL,
            match: (o) => isAbnormalAddress(o, addressFilterKeywords),
        },
        {
            id: "undeliverable",
            label: "不可达地址",
            level: PrintRuleLevel.OPTIONAL,
            match: (o) => !!checkUndeliverable(o, undeliverableAreaCodes),
        },
        {
            id: "double_address",
            label: "双地址",
            level: PrintRuleLevel.OPTIONAL,
            match: (o) =>
                o.tags?.includes(OrderTag.DOUBLE_ADDRESS) ||
                !!o.consignee?._streetDoubleAddressKeywords ||
                !!o.consignee?._localStreetDoubleAddressKeywords,
        },
        {
            id: "warehouse_warning",
            label: "库存预警",
            level: PrintRuleLevel.OPTIONAL,
            match: (o) =>
                o.orderItems.some((orderItem) => {
                    const externalId = orderItem.skuExternalId ?? orderItem.productExternalId;

                    return globalStore?.warehouseWarningStore.getIsInventoryWarning(externalId);
                }),
        },
        {
            id: "ali_can_not_ship",
            label: OrderTagNameMap[OrderTag.ALI_CAN_NOT_SHIP],
            level: PrintRuleLevel.OPTIONAL,
            match: (o) => isAnyOrderWithAliCanNotShipTag([o]),
        },
        {
            id: "taotian_paid_forbin_consign",
            label: OrderTagNameMap[OrderTag.TAOTIAN_PAID_FORBIN_CONSIGN],
            level: PrintRuleLevel.OPTIONAL,
            match: (o) => isAnyOrderWithTbCanNotShipTag([o]),
        },
        {
            id: "can_not_ship",
            label: OrderTagNameMap[OrderTag.CANNOT_SHIP],
            level: PrintRuleLevel.OPTIONAL,
            match: (o) => isAnyOrderWithCanNotShipTag([o]),
        },
        {
            id: "doudian_order_supply",
            label: OrderTagNameMap[OrderTag.DOUDIAN_ORDER_SUPPLY],
            level: PrintRuleLevel.OPTIONAL,
            match: (o) => o.tags?.includes(OrderTag.DOUDIAN_ORDER_SUPPLY),
        },

        // ====== 禁止打印 ======
        {
            id: "ali_managed_shipping",
            label: OrderTagNameMap[OrderTag.ALI_MANAGED_SHIPPING],
            level: PrintRuleLevel.FORBIDDEN,
            match: (o) => o.tags?.includes(OrderTag.ALI_MANAGED_SHIPPING),
        },
        {
            id: "ali_ae_door_pickup",
            label: OrderTagNameMap[OrderTag.ALI_AE_DOOR_PICKUP],
            level: PrintRuleLevel.FORBIDDEN,
            match: (o) => o.tags?.includes(OrderTag.ALI_AE_DOOR_PICKUP),
        },
        {
            id: "ali_ae_self_send",
            label: OrderTagNameMap[OrderTag.ALI_AE_SELF_SEND],
            level: PrintRuleLevel.FORBIDDEN,
            match: (o) => o.tags?.includes(OrderTag.ALI_AE_SELF_SEND),
        },
        {
            id: "jd_hyperlink_order",
            label: OrderTagNameMap[OrderTag.JD_HYPERLINK_ORDER],
            level: PrintRuleLevel.FORBIDDEN,
            match: (o) => o.tags?.includes(OrderTag.JD_HYPERLINK_ORDER),
        },
        {
            id: "jd_hyperlink_subsidy_order",
            label: OrderTagNameMap[OrderTag.JD_HYPERLINK_SUBSIDY_ORDER],
            level: PrintRuleLevel.FORBIDDEN,
            match: (o) => o.tags?.includes(OrderTag.JD_HYPERLINK_SUBSIDY_ORDER),
        },

        {
            id: "ali_fulfillment_by_warehouse",
            label: OrderTagNameMap[OrderTag.ALI_FULFILLMENT_BY_WAREHOUSE],
            level: PrintRuleLevel.FORBIDDEN,
            match: (o) => isAnyOrderWithAliWarehouse([o]),
        },
        {
            id: "pdd_fulfillment_by_shared_warehouse",
            label: OrderTagNameMap[OrderTag.PDD_FULFILLMENT_BY_SHARED_WAREHOUSE],
            level: PrintRuleLevel.FORBIDDEN,
            match: (o) => isAnyOrderWithPddWarehouse([o]),
        },

        {
            id: "jd_pet_item",
            label: OrderTagNameMap[OrderTag.JD_PET_ITEM],
            level: PrintRuleLevel.FORBIDDEN,
            match: (o) => isAnyOrderWithJDPetItem([o]),
        },
    ];
}
